import type { Translations } from "../../i18n/en";
import { pluralForm } from "../../i18n/plural";
import type {
    LastMinuteMood,
    MarketDriver,
    MarketMood,
    MonthPace,
    OutlookMonth,
    OutlookNight,
    PricingAction,
    YearOverYear,
} from "../../services/outlookApi";

/**
 * components/dashboard/outlookCopy.ts
 *
 * The words and tones of the dashboard's outlook.
 *
 * The server answers with moods (`services/outlookApi.ts`); this module is the
 * one place a mood becomes a sentence and a colour, so the status bar and the
 * blocks under it cannot word the same night two ways. The outlook was asked
 * for as a mood, and a figure slipped into a sentence would make it the
 * dashboard it replaced — so it prints one count and no other: the rooms a
 * night still has free (`describeNightSales`), which its first reader asked for
 * the day it shipped, "last rooms" reading the same over one room and over four.
 *
 * Every word and tone here is read through `listed`, never straight out of a
 * table: the server's lists are not this page's.
 */

/** The outlook's own strings. */
export type OutlookStrings = Translations["dashboard"]["outlook"];

/**
 * Whether a mood is good news. `neutral` is a state with no verdict attached;
 * `unknown` is no reading at all, and is drawn hollow so it cannot be taken
 * for either.
 */
export type OutlookTone = "good" | "neutral" | "caution" | "bad" | "unknown";

/**
 * What a table holds for a value the server sent, or null where this page
 * lists no such value.
 *
 * The unions in `services/outlookApi.ts` list the values this page was built
 * with, which is not what the server may send: the two are released apart and
 * a tab stays open for days, so every mood the server gains is met by pages
 * that predate it — the rule the sync indicator was rewritten for (4 Oct
 * 2026). Read straight out of a table, such a value came back `undefined`: an
 * empty label beside its mark, the text "undefined" in the status line, a
 * market drawn green because it was neither soft nor weak, and a throw on a
 * demand level that took the whole dashboard down.
 *
 * So a value this page does not list is read as no reading at all, and each
 * reader says of it what it already says where nothing can be claimed: "No
 * data", "No comparison yet", a dash, on the hollow mark. That is what an older
 * page can truthfully draw, and never how a value ships:
 * `outlook-moods.parity.test.tsx` holds this page's lists and words to the
 * server's.
 *
 * The table is still typed by the value's own union, so the compiler goes on
 * refusing a value added to the page's list without its word or its tone; what
 * comes back is read as a string or as nothing. Not with `??` alone: a value
 * called `constructor` reads back `Object`.
 */
export function listed<K extends string, V extends string>(
    table: Readonly<Record<K, V>>,
    value: K | null | undefined,
): V | null {
    if (typeof value !== "string") return null;
    const found: unknown = table[value];
    return typeof found === "string" && found !== "" ? (found as V) : null;
}

/**
 * Past this, the prices the outlook describes are older than the slowest sync
 * leaves them (a day), with two hours for a run that started late.
 */
export const OUTLOOK_STALE_AFTER_MS = 26 * 60 * 60 * 1000;

/**
 * Whether the outlook's calculation is too old to be read as today's.
 *
 * Everything the outlook says was read off the last calculation, so a property
 * whose sync stopped two days ago would otherwise wear a calm mood over stale
 * prices. One rule for every surface that words the outlook — the dashboard's
 * nights and the rail's sync chip — so the two cannot call one calculation
 * fresh and stale at once. A calculation nobody recorded, or one the page
 * cannot date, is not called stale: nothing says how old it is.
 */
export function isOutlookStale(calculatedAt: string | null | undefined, now: Date = new Date()): boolean {
    if (typeof calculatedAt !== "string") return false;
    const at = Date.parse(calculatedAt);
    return Number.isFinite(at) && now.getTime() - at > OUTLOOK_STALE_AFTER_MS;
}

const NIGHT_NAMES = ["today", "tomorrow", "dayAfter"] as const;

/** Tonight, tomorrow night, the night after. */
export function nightName(o: OutlookStrings, index: number): string {
    const key = NIGHT_NAMES[index];
    return key ? o.nightNames[key] : "";
}

/** A month named on its own — "Říjen", "October" — as a heading names it. */
export function monthName(locale: string, year: number, month: number): string {
    const name = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: "long" });
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
}

function lowerFirst(text: string, locale?: string): string {
    return text.charAt(0).toLocaleLowerCase(locale) + text.slice(1);
}

/** The rooms a night still has free, as printed and as read out. */
export interface RoomsFigure {
    /** The count beside the mood: "4/31". */
    text: string;
    /** The same count in words — a screen reader says a bare fraction as a date. */
    label: string;
}

export interface NightSalesDescription {
    headline: string;
    /** Null where the words already say the count, or nobody counted the rooms. */
    figure: RoomsFigure | null;
    detail: string | null;
}

/** Down to a single free room: "last rooms" said of one room reads the same as of four. */
function isLastRoom(night: OutlookNight): boolean {
    return night.occupancy === "lastRooms" && night.rooms?.free === 1;
}

function describeRooms(o: OutlookStrings, locale: string, night: OutlookNight): RoomsFigure | null {
    const rooms = night.rooms;
    if (!rooms || !night.occupancy || !(rooms.total > 0) || !(rooms.free > 0)) return null;
    // The last room is counted by its own words.
    if (night.occupancy === "soldOut" || isLastRoom(night)) return null;

    const fill = (template: string) => template
        .replace("{free}", String(rooms.free))
        .replace("{total}", String(rooms.total));
    return {
        // The figure counts the rooms free in every mood. Beside the one mood
        // that names the rooms taken it says so: "Well booked 9/31" reads as
        // nine rooms sold.
        text: fill(night.occupancy === "busy" ? o.rooms.figureFree : o.rooms.figure),
        label: fill(pluralForm(locale, rooms.free, o.rooms.label)),
    };
}

/** How full the night is, how many rooms it has left, and how it sells against what is usual. */
export function describeNightSales(t: Translations, locale: string, night: OutlookNight): NightSalesDescription {
    const o = t.dashboard.outlook;
    const mood = listed(o.occupancy, night.occupancy);
    // The calendar's own word for the level, so the dashboard and the
    // Prices page call one night's demand by one name.
    const level = listed(t.pricingCalendar.demand, night.demand);
    return {
        headline: mood === null ? o.occupancy.unknown : isLastRoom(night) ? o.occupancy.lastRoom : mood,
        // Which way the count reads is the mood's to say, so a night whose
        // mood cannot be named prints none: "not read yet" beside "4/31"
        // would be two answers.
        figure: mood === null ? null : describeRooms(o, locale, night),
        detail: level === null ? null : o.demand.replace("{level}", lowerFirst(level)),
    };
}

/** What Optivo did with a night's prices, as its row says it. */
export function pricingHeadline(o: OutlookStrings, action: PricingAction | null): string {
    return listed(o.pricing, action) ?? o.pricing.unknown;
}

/** The same for tonight, as the status line says it. */
export function pricingStatus(o: OutlookStrings, action: PricingAction | null): string {
    return listed(o.pricingStatus, action) ?? o.pricingStatus.unknown;
}

/**
 * A calm market is the reassuring answer — nothing unusual — so it reads as
 * good news beside a favourable and a strong one; the words tell them apart.
 */
const MARKET_TONE: Record<MarketMood, OutlookTone> = {
    strong: "good",
    favourable: "good",
    calm: "good",
    soft: "caution",
    weak: "bad",
};

/**
 * Whether a night's market is good news. A mood this page cannot name is no
 * reading: every mood that was neither soft nor weak used to be drawn green,
 * which is reassurance on no evidence.
 */
export function marketTone(mood: MarketMood | null): OutlookTone {
    return listed(MARKET_TONE, mood) ?? "unknown";
}

/** A night's market, as its row says it. */
export function marketHeadline(o: OutlookStrings, mood: MarketMood | null): string {
    return listed(o.market, mood) ?? o.market.unknown;
}

/** Tonight's market, as the status line says it. */
export function marketStatus(o: OutlookStrings, mood: MarketMood | null): string {
    return listed(o.marketStatus, mood) ?? o.marketStatus.unknown;
}

/**
 * Switched off is the curve's absence rather than a state of it, so it is
 * drawn hollow; every state of the curve is neutral — a discount on the last
 * free room is the plan working, not a verdict.
 */
const LAST_MINUTE_TONE: Record<LastMinuteMood, OutlookTone> = {
    off: "unknown",
    notOpen: "neutral",
    waiting: "neutral",
    discounting: "neutral",
    holdingDiscount: "neutral",
    atFloor: "neutral",
};

/**
 * Where last minute stands for a night; null where nothing can be said, which
 * the column draws as a dash — a mood this page cannot name included.
 */
export function lastMinuteHeadline(o: OutlookStrings, mood: LastMinuteMood | null): string | null {
    return listed(o.lastMinute, mood);
}

/** The mark that leads it. */
export function lastMinuteTone(mood: LastMinuteMood | null): OutlookTone {
    return listed(LAST_MINUTE_TONE, mood) ?? "unknown";
}

/**
 * A driver this page lists no words for is left out. It takes a `never`, so a
 * kind added to the page's list without a case in `describeDriver` fails the
 * build rather than vanishing here.
 */
function unlistedDriver(_kind: never): null {
    return null;
}

function describeDriver(t: Translations, driver: MarketDriver, lifting: boolean): string | null {
    const o = t.dashboard.outlook;
    switch (driver.kind) {
        case "schoolHoliday":
            return driver.labels.length > 0 ? driver.labels.join(", ") : o.drivers.schoolHoliday;
        case "specialDate":
            return driver.labels.length > 0 ? driver.labels.join(", ") : o.drivers.specialDate;
        case "weather":
            return lifting ? o.drivers.weatherUp : o.drivers.weatherDown;
        case "weatherExtreme": {
            // Named as the Market Conditions page names it; a kind added to
            // the feed before it has a name here is still extreme weather.
            const kinds = t.marketConditions.weatherExtremes.kinds as Record<string, string>;
            const named = driver.labels.map((label) => kinds[label]).filter((name): name is string => Boolean(name));
            return named.length > 0 ? named.join(", ") : o.drivers.weatherExtreme;
        }
        case "competitorRates":
            return lifting ? o.drivers.competitorRatesUp : o.drivers.competitorRatesDown;
        default:
            return unlistedDriver(driver.kind);
    }
}

/**
 * What is behind a market that is not calm, strongest first.
 *
 * Behind a market this page cannot name, nothing is named either: which way
 * the weather or the nearby rates push is read off the mood, and under "No
 * data" a holiday's name would explain nothing. A driver it cannot name is
 * left out, where an empty line used to stand for it.
 */
export function describeMarketDrivers(t: Translations, night: OutlookNight): string[] {
    const named = night.market === null || night.market === undefined || listed(MARKET_TONE, night.market) !== null;
    if (!named) return [];
    const lifting = night.market === "strong" || night.market === "favourable";
    return night.marketDrivers
        .map((driver) => describeDriver(t, driver, lifting))
        .filter((line): line is string => line !== null);
}

const YEAR_OVER_YEAR_TONE: Record<YearOverYear, OutlookTone> = {
    wellAhead: "good",
    ahead: "good",
    level: "neutral",
    behind: "caution",
    wellBehind: "bad",
};

const PACE_TONE: Record<MonthPace, OutlookTone> = {
    faster: "good",
    usual: "neutral",
    slower: "caution",
};

/** One reading of a month: its words and whether they are good news. */
export interface MonthReading {
    headline: string;
    tone: OutlookTone;
}

export interface MonthDescription extends MonthReading {
    /**
     * Revenue and occupancy against last year, each on its own. Null where the
     * month cannot be compared with last year at all — `note` says why.
     */
    parts: { revenue: MonthReading; occupancy: MonthReading } | null;
    note: string | null;
}

/**
 * A value's word and its tone, or the words for no reading on the hollow tone.
 * The two go together: a word without a tone, or a tone without a word, is a
 * value this page does not list.
 */
function readingOf<K extends string>(
    words: Readonly<Record<K, string>>,
    tones: Readonly<Record<K, OutlookTone>>,
    value: K | null,
    noReading: string,
): MonthReading {
    const headline = listed(words, value);
    const tone = listed(tones, value);
    return headline !== null && tone !== null ? { headline, tone } : { headline: noReading, tone: "unknown" };
}

/** One of the two things a month is compared by; a year that carries none says so. */
function describeMonthPart(o: OutlookStrings, mood: YearOverYear | null): MonthReading {
    return readingOf(o.monthPart, YEAR_OVER_YEAR_TONE, mood, o.monthPartUnknown);
}

/**
 * A month as a verdict, and revenue and occupancy each on its own.
 *
 * The comparison with last year leads, and while there is one the pace is not
 * mentioned: it measures the month against the year's average rather than
 * against last year, so a peak month can be behind the one and ahead of the
 * other at once, and two yardsticks side by side read as a contradiction. The
 * pace speaks only where last year cannot be compared.
 *
 * No verdict opens with a word the others share. They all began "Developing…"
 * once, and two months one under the other had to be read to their last word
 * to be told apart.
 *
 * A comparison this page cannot name is still a comparison the server made, so
 * the pace stays silent under it and last year is not said to be missing: the
 * verdict reads as nothing to compare with, on the hollow mark, and revenue
 * and occupancy are each still named where they can be.
 */
export function describeMonth(o: OutlookStrings, month: OutlookMonth): MonthDescription {
    if (month.vsLastYear) {
        return {
            ...readingOf(o.monthHeadline, YEAR_OVER_YEAR_TONE, month.vsLastYear, o.monthNoComparison),
            parts: {
                revenue: describeMonthPart(o, month.revenueVsLastYear),
                occupancy: describeMonthPart(o, month.occupancyVsLastYear),
            },
            note: null,
        };
    }

    return {
        ...readingOf(o.monthPace, PACE_TONE, month.pace, o.monthNoComparison),
        parts: null,
        note: o.monthDetailNoLastYear,
    };
}

/**
 * The month for the status bar: its name and its mood in one short line. It
 * speaks of the pace exactly where `describeMonth` does, whose tone leads it.
 */
export function monthStatus(o: OutlookStrings, locale: string, month: OutlookMonth): string {
    const mood = month.vsLastYear
        ? listed(o.yearOverYear, month.vsLastYear) ?? lowerFirst(o.monthNoComparison, locale)
        : lowerFirst(listed(o.monthPace, month.pace) ?? o.monthNoComparison, locale);
    return o.monthStatus
        .replace("{month}", monthName(locale, month.year, month.month))
        .replace("{mood}", mood);
}
